import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Music2, Plug, Copy, CheckCircle, ExternalLink, Trash2, Radio, Send, Plus, Cloud, Megaphone, Zap, LogIn, LogOut } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import useSpotifyAuth from "../../lib/useSpotifyAuth";

/**
 * IntegrationsAdminTab — admin-only UI to configure third-party integrations.
 *
 * Currently ships the Spotify card (Client ID only, since we use PKCE flow
 * in the browser — no Client Secret needed).  Future-proofed for additional
 * providers by grouping cards.
 */
export default function IntegrationsAdminTab() {
  return (
    <div data-testid="admin-integrations-tab" className="space-y-6">
      <div className="flex items-center gap-2">
        <Plug className="w-5 h-5 text-tv-accent" />
        <h2 className="font-display font-black text-2xl text-white">Integrations</h2>
      </div>
      <SpotifyCard />
      <CloudflareCard />
      <NetworkSocialsCard />
      <ZapierBridgeCard />
      <NotifyBlastCard />
    </div>
  );
}

function SpotifyCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clientId, setClientId] = useState("");
  const [copied, setCopied] = useState(false);
  const redirectUri = typeof window !== "undefined" ? `${window.location.origin}/spotify/callback` : "";
  // Spotify PKCE auth — same hook the host studio uses, so the token
  // persisted here works seamlessly in the DJ deck once the admin
  // navigates to /studio. We expose a Connect / Disconnect button right
  // inside the integration card so admins can verify the integration
  // works end-to-end without leaving the admin page.
  const auth = useSpotifyAuth();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/integrations");
      setCfg(data.spotify || { configured: false, client_id: "" });
      setClientId(data.spotify?.client_id || "");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!clientId.trim()) { toast.error("Paste your Spotify Client ID first"); return; }
    setSaving(true);
    try {
      await api.post("/admin/integrations/spotify", { client_id: clientId.trim() });
      toast.success("Spotify Client ID saved — hosts can now sign in.");
      // Clear SDK cache so the next login picks up the fresh ID
      try { localStorage.removeItem("tapin_spotify_cid"); } catch {}
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Remove Spotify integration? Hosts currently signed in will stay logged in until their token expires.")) return;
    try {
      await api.delete("/admin/integrations/spotify");
      toast.success("Spotify disconnected");
      setClientId(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const copyRedirect = async () => {
    try { await navigator.clipboard.writeText(redirectUri); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };

  if (loading) return <div className="text-white/60 text-sm">Loading integrations…</div>;

  return (
    <div data-testid="admin-integrations-spotify" className="bg-white/5 border border-white/10 rounded-lg p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
          <Music2 className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-black text-lg">Spotify</h3>
            {cfg?.configured ? (
              <span className="inline-flex items-center gap-1 bg-emerald-500/25 text-emerald-200 border border-emerald-500/40 text-[10px] font-black uppercase px-2 py-0.5 rounded-full">
                <CheckCircle className="w-3 h-3" /> Connected
              </span>
            ) : (
              <span className="bg-white/10 text-white/70 text-[10px] font-black uppercase px-2 py-0.5 rounded-full">Not configured</span>
            )}
          </div>
          <p className="text-white/60 text-sm mt-1">
            Let hosts sign in with their Spotify Premium account and play full tracks inside the DJ Studio. You only need to set this up once — every host then uses their own Spotify login.
          </p>
        </div>
      </div>

      {/* Step-by-step setup */}
      <div className="bg-black/40 rounded border border-white/10 p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-wider font-black text-white/60">3-step setup</div>

        {/* Step 1 — Create app */}
        <div>
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-tv-accent text-black font-black text-xs flex items-center justify-center">1</span>
            <div className="font-bold text-white text-sm">Create a Spotify app</div>
          </div>
          <div className="pl-7 mt-1 text-white/70 text-sm space-y-1.5">
            <div>Go to the Spotify Developer Dashboard, sign in with any Spotify account, and click <strong>Create app</strong>.</div>
            <a
              href="https://developer.spotify.com/dashboard/create"
              target="_blank" rel="noreferrer"
              data-testid="spotify-open-dashboard"
              className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-black text-xs px-3 py-1.5 rounded"
            >
              Open Spotify Dashboard <ExternalLink className="w-3 h-3" />
            </a>
            <div className="text-white/50 text-xs">App name: "Tap IN Radio" (or whatever you like). App description: "DJ mixing platform". Website: your Tap IN URL.</div>
          </div>
        </div>

        {/* Step 2 — Register redirect URI */}
        <div>
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-tv-accent text-black font-black text-xs flex items-center justify-center">2</span>
            <div className="font-bold text-white text-sm">Register this redirect URI</div>
          </div>
          <div className="pl-7 mt-1 text-white/70 text-sm space-y-1.5">
            <div>In the app's Settings, paste this exact URL into <strong>Redirect URIs</strong> and Save:</div>
            <div className="flex items-center gap-2">
              <code data-testid="spotify-redirect-uri" className="flex-1 min-w-0 bg-black/60 border border-white/10 rounded px-2 py-1.5 text-xs font-mono text-tv-accent truncate">{redirectUri}</code>
              <button
                data-testid="spotify-copy-redirect"
                onClick={copyRedirect}
                className="bg-fuchsia-500 hover:bg-fuchsia-400 text-black font-black text-xs px-2.5 py-1.5 rounded inline-flex items-center gap-1"
              ><Copy className="w-3 h-3" /> {copied ? "Copied!" : "Copy"}</button>
            </div>
            <div className="text-white/50 text-xs">Also tick the <strong>Web Playback SDK</strong> box in the app's "APIs used" list.</div>
          </div>
        </div>

        {/* Step 3 — Paste Client ID */}
        <div>
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-tv-accent text-black font-black text-xs flex items-center justify-center">3</span>
            <div className="font-bold text-white text-sm">Paste the Client ID here</div>
          </div>
          <div className="pl-7 mt-1 text-white/70 text-sm space-y-1.5">
            <div>Copy your app's <strong>Client ID</strong> from the dashboard (Settings → Basic Information) and paste it below. You do NOT need the Client Secret — we use the browser PKCE flow.</div>
            <div className="flex items-center gap-2">
              <input
                data-testid="spotify-client-id-input"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="e.g. 3a8f4b1c2d5e4f6a7b8c9d0e1f2a3b4c"
                className="flex-1 bg-black/60 border border-white/10 rounded px-3 py-2 text-white text-sm font-mono focus:border-tv-accent outline-none"
              />
              <button
                data-testid="spotify-save-client-id"
                onClick={save}
                disabled={saving || !clientId.trim()}
                className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black font-black text-sm px-4 py-2 rounded"
              >{saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      </div>

      {/* Step 4 — Sign in with Premium */}
      {cfg?.configured && (
        <div data-testid="spotify-admin-signin" className="border border-emerald-500/30 bg-emerald-500/5 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-emerald-500 text-black font-black text-sm flex items-center justify-center shrink-0">4</div>
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-bold text-white text-sm">Sign in with Spotify Premium</div>
                  <div className="text-white/60 text-xs mt-0.5">
                    Each host (and you, for testing) needs their OWN Premium login. Connect once and the studio decks can stream full tracks.
                  </div>
                </div>
                {auth.loggedIn ? (
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="inline-flex items-center gap-1.5 bg-emerald-500/20 text-emerald-200 text-[11px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Connected
                    </span>
                    <button
                      type="button"
                      data-testid="spotify-admin-signout"
                      onClick={() => { auth.logout(); toast.success("Signed out of Spotify"); }}
                      className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white font-black text-xs px-3 py-2 rounded"
                    >
                      <LogOut className="w-3.5 h-3.5" /> Sign out
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="spotify-admin-signin-btn"
                    onClick={async () => {
                      try { await auth.login(); }
                      catch (e) { toast.error(e.message || "Spotify sign-in failed"); }
                    }}
                    disabled={!auth.configured}
                    className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black font-black text-sm px-4 py-2 rounded shrink-0"
                  >
                    <LogIn className="w-4 h-4" /> Sign in with Spotify
                  </button>
                )}
              </div>
              {auth.loggedIn && auth.token?.expires_at && (
                <div className="text-[10px] text-white/40">
                  Token expires {new Date(auth.token.expires_at).toLocaleTimeString()} (auto-refreshes)
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Disconnect footer */}
      {cfg?.configured && (
        <div className="flex items-center justify-between bg-white/5 rounded p-3">
          <div className="text-white/60 text-xs">
            Current Client ID: <code className="text-white/80 font-mono">{cfg.client_id.slice(0, 8)}…{cfg.client_id.slice(-4)}</code>
            {cfg.updated_at && <span className="ml-2">· updated {new Date(cfg.updated_at).toLocaleDateString()}</span>}
          </div>
          <button
            data-testid="spotify-disconnect"
            onClick={disconnect}
            className="bg-red-500/20 hover:bg-red-500/40 text-red-200 font-black text-xs px-3 py-1.5 rounded inline-flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" /> Disconnect
          </button>
        </div>
      )}

      {/* Host tip */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-[11px] leading-snug text-amber-100/90">
        <strong>Heads-up:</strong> each host who wants to play Spotify inside the studio needs their own <strong>Spotify Premium</strong> account — Spotify doesn't allow the Web Playback SDK to play full tracks for Free accounts.
      </div>
    </div>
  );
}

// ── Cloudflare credentials card ─────────────────────────────────────────
function CloudflareCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({ account_id: "", api_token: "" });

  const tokenCreateUrl =
    "https://dash.cloudflare.com/profile/api-tokens?permissionGroupKeys=%5B%7B%22key%22%3A%22stream%22%2C%22type%22%3A%22edit%22%7D%5D&name=Tap%20IN%20TV%20Stream";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/integrations");
      setCfg(data.cloudflare || { configured: false, account_id: "", token_masked: "" });
      setForm({ account_id: data.cloudflare?.account_id || "", api_token: "" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.account_id.trim() || !form.api_token.trim()) {
      toast.error("Paste both Account ID and API Token first"); return;
    }
    setSaving(true);
    try {
      await api.post("/admin/integrations/cloudflare", {
        account_id: form.account_id.trim(), api_token: form.api_token.trim(),
      });
      toast.success("Cloudflare connected — Stream RTMP ingest now available.");
      setForm((f) => ({ ...f, api_token: "" }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Cloudflare Stream? Any live inputs already provisioned will keep running on Cloudflare's side until you delete them.")) return;
    try {
      await api.delete("/admin/integrations/cloudflare");
      toast.success("Cloudflare disconnected");
      setForm({ account_id: "", api_token: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const testPing = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/integrations/cloudflare/test");
      if (data.ok) toast.success(data.message || "Cloudflare Stream reachable");
      else toast.error(`Ping failed: ${data.error || data.status || "unknown"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting(false); }
  };

  if (loading) return <div className="text-white/60 text-sm">Loading Cloudflare…</div>;

  return (
    <div data-testid="admin-integrations-cloudflare" className="bg-white/5 border border-white/10 rounded-lg p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded bg-orange-500/20 border border-orange-500/40 flex items-center justify-center shrink-0">
          <Cloud className="w-5 h-5 text-orange-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-black text-lg">Cloudflare Stream</h3>
            {cfg?.configured ? (
              <span className="inline-flex items-center gap-1 bg-emerald-500/25 text-emerald-200 border border-emerald-500/40 text-[10px] font-black uppercase px-2 py-0.5 rounded-full">
                <CheckCircle className="w-3 h-3" /> Connected
              </span>
            ) : (
              <span className="bg-white/10 text-white/70 text-[10px] font-black uppercase px-2 py-0.5 rounded-full">Not configured</span>
            )}
          </div>
          <p className="text-white/60 text-sm mt-1">
            Real RTMP ingest, global HLS playback, automatic recording and
            viewer analytics for live broadcasts. Creators paste their stream
            key into OBS / Streamlabs / mobile broadcaster and hit go live.
          </p>
        </div>
      </div>

      <div className="bg-black/40 rounded border border-white/10 p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-wider font-black text-white/60">2-step setup</div>

        {/* Step 1 — API token */}
        <div>
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-orange-400 text-black font-black text-xs flex items-center justify-center">1</span>
            <div className="font-bold text-white text-sm">Create a Stream API token</div>
          </div>
          <div className="pl-7 mt-1 text-white/70 text-sm space-y-1.5">
            <div>Cloudflare doesn't support OAuth for the Stream API. We use a scoped API token instead. Click below — the form is pre-filled with the right permission (<code className="text-white/90">Stream : Edit</code>).</div>
            <a
              href={tokenCreateUrl}
              target="_blank" rel="noreferrer"
              data-testid="cloudflare-open-token-page"
              className="inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-400 text-black font-black text-xs px-3 py-1.5 rounded"
            >
              Open Cloudflare token page <ExternalLink className="w-3 h-3" />
            </a>
            <div className="text-white/50 text-xs">Review the permissions, click <strong>Continue to summary</strong>, then <strong>Create Token</strong>. Cloudflare shows the token exactly once — copy it.</div>
          </div>
        </div>

        {/* Step 2 — Paste creds */}
        <div>
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-orange-400 text-black font-black text-xs flex items-center justify-center">2</span>
            <div className="font-bold text-white text-sm">Paste Account ID + Token</div>
          </div>
          <div className="pl-7 mt-1 text-white/70 text-sm space-y-1.5">
            <div>
              Your Account ID is visible at <a className="text-orange-300 underline" href="https://dash.cloudflare.com/?to=/:account/stream" target="_blank" rel="noreferrer">dash.cloudflare.com</a> → Stream → right sidebar.
            </div>
            <input
              data-testid="cloudflare-account-id-input"
              value={form.account_id}
              onChange={(e) => setForm({ ...form, account_id: e.target.value })}
              placeholder="Account ID (32-char hex)"
              className="w-full bg-black/60 border border-white/10 rounded px-3 py-2 text-white text-sm font-mono focus:border-orange-400 outline-none"
            />
            <input
              data-testid="cloudflare-api-token-input"
              value={form.api_token}
              onChange={(e) => setForm({ ...form, api_token: e.target.value })}
              placeholder={cfg?.configured ? `New API token (leave blank to keep ${cfg.token_masked})` : "API Token"}
              type="password"
              className="w-full bg-black/60 border border-white/10 rounded px-3 py-2 text-white text-sm font-mono focus:border-orange-400 outline-none"
            />
            <div className="flex gap-2">
              <button
                data-testid="cloudflare-save"
                onClick={save}
                disabled={saving || !form.account_id.trim() || !form.api_token.trim()}
                className="bg-orange-500 hover:bg-orange-400 disabled:opacity-40 text-black font-black text-sm px-4 py-2 rounded"
              >{saving ? "Saving…" : "Save & connect"}</button>
              {cfg?.configured && (
                <button
                  data-testid="cloudflare-test"
                  onClick={testPing} disabled={testing}
                  className="bg-white/10 hover:bg-white/20 text-white font-black text-sm px-3 py-2 rounded inline-flex items-center gap-1"
                >{testing ? "Pinging…" : "Test connection"}</button>
              )}
            </div>
          </div>
        </div>
      </div>

      {cfg?.configured && (
        <div className="flex items-center justify-between bg-white/5 rounded p-3">
          <div className="text-white/60 text-xs">
            Account: <code className="text-white/80 font-mono">{cfg.account_id}</code>
            {cfg.token_masked && <span className="ml-2">· token <code className="text-white/80 font-mono">{cfg.token_masked}</code></span>}
            {cfg.updated_at && <span className="ml-2">· updated {new Date(cfg.updated_at).toLocaleDateString()}</span>}
          </div>
          <button
            data-testid="cloudflare-disconnect"
            onClick={disconnect}
            className="bg-red-500/20 hover:bg-red-500/40 text-red-200 font-black text-xs px-3 py-1.5 rounded inline-flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" /> Disconnect
          </button>
        </div>
      )}

      <div className="bg-orange-500/10 border border-orange-500/30 rounded p-3 text-[11px] leading-snug text-orange-100/90">
        <strong>Billing note:</strong> Cloudflare Stream is billed per minute of video stored and per 1,000 minutes delivered. The free trial covers small tests — production usage bills at their published rates on your Cloudflare account.
      </div>
    </div>
  );
}

const NETWORK_KINDS = [
  { key: "discord", label: "Discord webhook", hint: "Your Tap IN TV Discord server → Server Settings → Integrations → Webhooks → New Webhook" },
  { key: "slack",   label: "Slack webhook",   hint: "Tap IN TV Slack workspace → Incoming Webhooks" },
  { key: "zapier",  label: "Zapier catch hook", hint: "Zapier → Trigger 'Catch Hook' → copy webhook URL (lets you fan out to Twitter/X, Instagram, Facebook, etc.)" },
  { key: "make",    label: "Make.com webhook", hint: "Make.com scenario → Webhook module → copy URL" },
  { key: "webhook", label: "Generic POST",     hint: "Any HTTPS endpoint that accepts JSON" },
];

function NetworkSocialsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingTpl, setSavingTpl] = useState(false);
  const [template, setTemplate] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ label: "", kind: "discord", url: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/integrations/network-socials");
      setData(data); setTemplate(data.template || "");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveTpl = async () => {
    setSavingTpl(true);
    try {
      await api.post("/admin/integrations/network-socials/config", { template });
      toast.success("Network template saved"); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingTpl(false); }
  };
  const addHook = async () => {
    if (!form.label || !form.url) { toast.error("Fill label + URL"); return; }
    try {
      await api.post("/admin/integrations/network-socials/hooks", { ...form, enabled: true });
      setForm({ label: "", kind: "discord", url: "" }); setAdding(false); load();
      toast.success("Network webhook added — fires on every host's Go Live");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggle = async (h) => {
    try { await api.patch(`/admin/integrations/network-socials/hooks/${h.id}`, { enabled: !h.enabled }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (h) => {
    if (!window.confirm(`Remove "${h.label}" from network socials?`)) return;
    try { await api.delete(`/admin/integrations/network-socials/hooks/${h.id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const testFire = async () => {
    try {
      const { data } = await api.post("/admin/integrations/network-socials/test");
      const ok = (data.results || []).filter((r) => r.ok).length;
      toast.success(`Test fired: ${ok} OK · ${(data.count || 0) - ok} failed`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !data) return <div className="text-white/60 text-sm">Loading network socials…</div>;

  const hooks = data.hooks || [];
  return (
    <div data-testid="admin-integrations-network" className="bg-white/5 border border-white/10 rounded-lg p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded bg-fuchsia-500/20 border border-fuchsia-500/40 flex items-center justify-center shrink-0">
          <Radio className="w-5 h-5 text-fuchsia-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-black text-lg">Tap IN TV Socials</h3>
            {hooks.filter(h => h.enabled).length > 0 ? (
              <span className="inline-flex items-center gap-1 bg-emerald-500/25 text-emerald-200 border border-emerald-500/40 text-[10px] font-black uppercase px-2 py-0.5 rounded-full">
                <CheckCircle className="w-3 h-3" /> {hooks.filter(h => h.enabled).length} live
              </span>
            ) : (
              <span className="bg-white/10 text-white/70 text-[10px] font-black uppercase px-2 py-0.5 rounded-full">Not configured</span>
            )}
          </div>
          <p className="text-white/60 text-sm mt-1">
            Network-wide webhooks fired on <strong>every host's</strong> Go Live — your Tap IN TV official socials. Hosts' personal webhooks fire in addition to these.
          </p>
        </div>
      </div>

      {/* Template */}
      <div className="bg-black/40 rounded border border-white/10 p-3 space-y-2">
        <div className="text-[10px] uppercase tracking-wider font-black text-white/60">Network announcement template</div>
        <textarea
          data-testid="network-template"
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          rows={3}
          className="w-full bg-black/60 border border-white/10 rounded px-3 py-2 text-white text-sm resize-none font-mono"
        />
        <div className="text-white/50 text-[11px]">
          Placeholders: <code>{"{show_name}"}</code> <code>{"{host_name}"}</code> <code>{"{station}"}</code> <code>{"{listen_url}"}</code>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="network-save-template"
            onClick={saveTpl} disabled={savingTpl}
            className="bg-fuchsia-500 hover:bg-fuchsia-400 disabled:opacity-40 text-black font-black text-xs px-3 py-1.5 rounded"
          >{savingTpl ? "Saving…" : "Save template"}</button>
          <button
            data-testid="network-test-fire"
            onClick={testFire} disabled={hooks.filter(h => h.enabled).length === 0}
            className="bg-yellow-400/25 hover:bg-yellow-400/50 disabled:opacity-30 text-yellow-100 font-black text-xs px-3 py-1.5 rounded inline-flex items-center gap-1"
          ><Send className="w-3 h-3" /> Test fire</button>
        </div>
      </div>

      {/* Hooks list */}
      <div className="space-y-1.5" data-testid="network-hooks-list">
        {hooks.length === 0 && <div className="text-white/40 text-sm text-center py-4">No network webhooks yet. Add one below to broadcast every Go Live to your Tap IN TV audience.</div>}
        {hooks.map((h) => (
          <div key={h.id} data-testid={`network-hook-${h.id}`} className="bg-white/5 rounded p-2 flex items-center gap-2">
            <input type="checkbox" checked={!!h.enabled} onChange={() => toggle(h)} data-testid={`network-toggle-${h.id}`} className="accent-fuchsia-500 w-4 h-4" />
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-bold truncate">{h.label}</div>
              <div className="text-white/50 text-xs truncate">{h.kind} · {h.url}</div>
            </div>
            <button onClick={() => del(h)} className="bg-red-500/20 hover:bg-red-500/40 text-red-200 p-1.5 rounded">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      {/* Add form */}
      {adding ? (
        <div className="bg-black/40 border border-white/10 rounded p-3 space-y-2" data-testid="network-add-form">
          <input
            data-testid="network-add-label"
            placeholder="Label (e.g. 'Tap IN TV Discord')"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            className="w-full bg-black/60 border border-white/10 rounded px-3 py-2 text-white text-sm"
          />
          <select
            data-testid="network-add-kind"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            className="w-full bg-black/60 border border-white/10 rounded px-3 py-2 text-white text-sm"
          >
            {NETWORK_KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
          <div className="text-white/50 text-xs">{NETWORK_KINDS.find(k => k.key === form.kind)?.hint}</div>
          <input
            data-testid="network-add-url"
            placeholder="Webhook URL"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            className="w-full bg-black/60 border border-white/10 rounded px-3 py-2 text-white text-sm font-mono"
          />
          <div className="flex gap-2">
            <button
              data-testid="network-add-save"
              onClick={addHook}
              className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black font-black text-sm py-2 rounded"
            >Save webhook</button>
            <button onClick={() => setAdding(false)} className="bg-white/5 hover:bg-white/15 text-white font-black text-sm px-4 py-2 rounded">Cancel</button>
          </div>
        </div>
      ) : (
        <button
          data-testid="network-add-open"
          onClick={() => setAdding(true)}
          className="w-full inline-flex items-center justify-center gap-1 bg-white/5 hover:bg-white/15 text-white/80 font-black text-sm py-2 rounded border border-dashed border-white/20"
        >
          <Plus className="w-4 h-4" /> Add Tap IN TV webhook
        </button>
      )}

      <div className="bg-fuchsia-500/10 border border-fuchsia-500/30 rounded p-3 text-[11px] leading-snug text-fuchsia-100/90">
        <strong>Good to know:</strong> to post to Twitter/X, Facebook, or Instagram automatically, use a Zapier or Make.com webhook — those platforms don't expose simple webhook endpoints. Zapier's free tier is enough for 1-3 Go Lives per day.
      </div>
    </div>
  );
}


// ── One-off "Notify blast" (fires all network webhooks with custom msg) ──
function NotifyBlastCard() {
  const [msg, setMsg] = useState("");
  const [category, setCategory] = useState("network");
  const [sending, setSending] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const [confirming, setConfirming] = useState(false);

  const blast = async () => {
    if (!msg.trim()) { toast.error("Write a message first"); return; }
    if (!confirming) {
      // Two-step confirmation (replaces window.confirm which isn't
      // triggerable in the testing harness + feels heavier than needed).
      setConfirming(true);
      setTimeout(() => setConfirming(false), 4000);
      return;
    }
    setConfirming(false);
    setSending(true);
    try {
      const { data } = await api.post("/admin/notify-blast", { message: msg.trim(), category });
      const ok = data.ok || 0;
      const total = data.count || 0;
      setLastRun({ ok, total, when: new Date().toLocaleTimeString() });
      if (total === 0) toast.info(data.note || "No webhooks configured");
      else toast.success(`Blast fired: ${ok} / ${total} webhooks OK`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="admin-notify-blast-card" className="bg-white/5 border border-white/10 rounded-lg p-5 space-y-3">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0">
          <Megaphone className="w-5 h-5 text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-black text-lg">Notify blast</h3>
          <p className="text-white/60 text-sm mt-1">
            One-off broadcast to every enabled Tap IN TV network webhook. Uses the same Discord / Slack / Zapier endpoints you configured above — no new integration key required.
          </p>
        </div>
      </div>

      <textarea
        data-testid="notify-blast-message"
        value={msg}
        onChange={(e) => setMsg(e.target.value)}
        rows={3}
        placeholder="e.g. 🚨 Next Top Creator finalists drop tonight at 8 PM ET on Tap IN TV — vote early!"
        className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm resize-none"
      />

      <div className="flex items-center gap-2 flex-wrap">
        <label className="inline-flex items-center gap-1.5 text-sm">
          Category
          <select
            data-testid="notify-blast-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs"
          >
            <option value="network">Network</option>
            <option value="contest">Contest</option>
            <option value="radio">Radio</option>
            <option value="custom">Custom</option>
          </select>
        </label>
        <button
          data-testid="notify-blast-send"
          onClick={blast}
          disabled={sending || !msg.trim()}
          className={`ml-auto ${confirming ? "bg-red-500 hover:bg-red-400" : "bg-amber-500 hover:bg-amber-400"} disabled:opacity-40 text-black font-black text-sm px-4 py-2 rounded inline-flex items-center gap-1.5`}
        ><Send className="w-4 h-4" /> {sending ? "Firing…" : confirming ? "Click again to confirm" : "Send blast"}</button>
      </div>

      {lastRun && (
        <div className="text-white/50 text-[11px]">
          Last blast: {lastRun.ok} / {lastRun.total} OK at {lastRun.when}
        </div>
      )}
    </div>
  );
}

// ── Zapier bridge (free path to email/SMS/Twitter via Zaps) ─────────────
function ZapierBridgeCard() {
  const [copied, setCopied] = useState(false);
  const samplePayload = JSON.stringify({
    template: "🔴 {host_name} is live on {station} — {show_name}. Tune in: {listen_url}",
    message:  "🔴 RadioDJ is live on Blaze FM — Friday Happy Hour. Tune in: https://tapintv.com/radio/station/blaze-fm",
    show_name: "Friday Happy Hour",
    host_name: "RadioDJ",
    station:  "Blaze FM",
    listen_url: "https://tapintv.com/radio/station/blaze-fm",
    started_at: new Date().toISOString(),
    destinations: ["zapier-email-blast"],
  }, null, 2);

  const copy = async () => {
    try { await navigator.clipboard.writeText(samplePayload); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };

  // Zapier doesn't have an OAuth-style "create hook in our name" button, but
  // it *does* support deep-linking into the Zap editor pre-seeded with a
  // Webhooks by Zapier trigger. Users still need a Zapier account.
  const zapierLoginUrl = "https://zapier.com/app/login";
  const zapierNewZapUrl = "https://zapier.com/app/editor/template?template=14881";
  const zapierWebhookDocsUrl = "https://zapier.com/apps/webhook/help";

  return (
    <div data-testid="admin-zapier-bridge-card" className="bg-gradient-to-br from-orange-500/10 to-fuchsia-500/10 border border-orange-500/30 rounded-lg p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded bg-orange-500/20 border border-orange-500/40 flex items-center justify-center shrink-0">
          <Zap className="w-5 h-5 text-orange-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-black text-lg">Zapier bridge → Email / SMS / X / anywhere</h3>
          <p className="text-white/70 text-sm mt-1">
            Skip Twilio/Resend setup entirely. Paste a Zapier "Catch Hook" URL above under <strong>Tap IN TV Socials</strong> (kind: Zapier catch hook) and Zapier forwards every blast to whatever action you pick: Gmail / Mailchimp / Twitter / ClickSend SMS / Slack / Notion. Zapier's free tier covers ~100 tasks/mo — plenty for launch announcements.
          </p>
        </div>
      </div>

      <div className="bg-black/40 rounded border border-white/10 p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-wider font-black text-white/60">3-step setup</div>

        <ZapierStep num="1" title="Sign in to Zapier (or create a free account)">
          <a
            data-testid="zapier-signin-link"
            href={zapierLoginUrl} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-400 text-black font-black text-xs px-3 py-1.5 rounded"
          >Open Zapier <ExternalLink className="w-3 h-3" /></a>
          <span className="ml-2 text-white/50 text-xs">Free tier covers this use case.</span>
        </ZapierStep>

        <ZapierStep num="2" title="Create a new Zap with 'Webhooks by Zapier' as the trigger">
          <div className="flex flex-wrap gap-2">
            <a
              data-testid="zapier-new-zap-link"
              href={zapierNewZapUrl} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white font-black text-xs px-3 py-1.5 rounded"
            >Open Zap editor <ExternalLink className="w-3 h-3" /></a>
            <a
              href={zapierWebhookDocsUrl} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-orange-300 hover:text-orange-200 text-xs font-bold"
            >Webhooks help <ExternalLink className="w-3 h-3" /></a>
          </div>
          <div className="text-white/60 text-xs mt-1.5">
            In Zapier: App = <strong>Webhooks by Zapier</strong>, Event = <strong>Catch Hook</strong>. Copy the URL Zapier gives you.
          </div>
        </ZapierStep>

        <ZapierStep num="3" title="Paste that URL above under Tap IN TV Socials → Add new → Kind: Zapier catch hook">
          <div className="text-white/60 text-xs">Then pick your action in Zapier (Gmail / Twitter / ClickSend SMS / etc.) and map our payload fields to the action.</div>
        </ZapierStep>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <div className="text-[10px] uppercase tracking-wider font-black text-white/60 flex-1">Sample payload Zapier will receive</div>
          <button
            data-testid="zapier-copy-payload"
            onClick={copy}
            className="text-[10px] font-black bg-white/10 hover:bg-white/20 text-white px-2 py-1 rounded inline-flex items-center gap-1"
          >{copied ? <><CheckCircle className="w-3 h-3 text-emerald-300" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}</button>
        </div>
        <pre className="bg-black/60 border border-white/10 rounded text-white/80 text-[11px] font-mono p-3 overflow-x-auto max-h-60">{samplePayload}</pre>
        <div className="text-white/50 text-[11px] mt-1.5">
          Map <code className="text-white/80">message</code> to the email body or SMS text in your Zap. Use <code className="text-white/80">host_name</code> / <code className="text-white/80">station</code> / <code className="text-white/80">listen_url</code> in the subject line for a richer email.
        </div>
      </div>

      <div className="bg-orange-500/10 border border-orange-500/30 rounded p-3 text-[11px] leading-snug text-orange-100/90">
        <strong>Why this is actually good:</strong> every time you add a new channel (Discord, Telegram, Facebook Page, X, Bluesky, email list, SMS list), you just add another Zap — zero code changes on our side.
      </div>
    </div>
  );
}

function ZapierStep({ num, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="w-5 h-5 rounded-full bg-orange-400 text-black font-black text-xs flex items-center justify-center">{num}</span>
        <div className="font-bold text-white text-sm">{title}</div>
      </div>
      <div className="pl-7 mt-1">{children}</div>
    </div>
  );
}

